import { getObjectModelName } from 'services/FieldService';
import { compose } from 'redux';
import { isOwnerField } from 'checks/fields';
import { isVariableInitialization } from 'pages/AutomationEngine/dialogs/VariableWizard/utilities';
import { createSelector } from '@reduxjs/toolkit';
import { isTypeGlobal, isTypeRecordBased } from 'pages/Automations/helpers';
import { getStepConfig, STEP_NODE_TYPES } from 'pages/AutomationEngine/steps';

export const getLoading = ({ loading }) => loading > 0;

export const getFieldsLoaded = ({ fieldsLoaded }) => fieldsLoaded;

export const getEnableUI = ({ loading, fieldsLoaded, refetching }) =>
  loading === 0 && fieldsLoaded && refetching === 0;

export const getShowStats = ({ showStats, executionMode }) =>
  showStats && !executionMode;

export const getStatsRefreshEnabled = ({ statsRefreshEnabled }) =>
  statsRefreshEnabled;

export const getDirty = ({ dirty }) => dirty;

export const getPlacing = ({ placing }) => placing;
export const getPlacingVariable = ({ placingVariable }) => placingVariable;

export const getLastPlacingStep = ({ lastPlacingStep }) => lastPlacingStep;

export const getLastPlacingMergeStep = ({ lastPlacingMergeStep }) =>
  lastPlacingMergeStep;
export const getMergeStepsWithChidren = ({ mergeStepsWithChidren }) =>
  mergeStepsWithChidren;
export const getMergeAddNodesWhileMoving = ({ mergeAddNodesWhileMoving }) =>
  mergeAddNodesWhileMoving;
export const getTrailingBranchToHide = ({ trailingBranchToHide }) =>
  trailingBranchToHide;

export const lastDroppingStep = ({ lastDroppingStep: step }) => step;
export const getPrevSteps = ({ prevSteps }) => prevSteps;

export const getSteps = ({ steps }) => steps;
export const getTriggers = ({ triggers }) => triggers;
export const getVariables = ({ variables }) => variables;
export const getSavedVariables = ({ automation }) => automation.variables;
export const getMetaData = ({ metaData }) => metaData;

export const getProviderModelLookup = createSelector(getMetaData, (metaData) =>
  (metaData?.llm?.provider_model_details ?? []).reduce((acc, provider) => {
    (provider.models ?? []).forEach((model) => {
      acc[model.model_value] = model.model_label;
    });
    return acc;
  }, {})
);

export const getStepById = ({ steps }, id) =>
  steps.filter((step) => step.id === id);

export const getSaveable = ({
  loading,
  persisting,
  dirty,
  errorSteps,
  nameError,
}) =>
  loading === 0 &&
  persisting === 0 &&
  dirty &&
  (errorSteps || []).length === 0 &&
  !nameError;

export const getEnableDebugger = ({
  loading,
  persisting,
  dirty,
  errorSteps,
  nameError,
  automation: { active },
}) =>
  loading === 0 &&
  persisting === 0 &&
  !dirty &&
  active &&
  (errorSteps || []).length === 0 &&
  !nameError;

export const getAutomationCustomObjectClass = ({ automation }) =>
  automation.customObject?.objectClass;

export const getAutomationCustomObject = ({ automation }) =>
  automation.customObject;

export const getAutomation = ({ automation }) => automation;
export const getAutomationCustomObjectFields = ({ fields }) => fields;

export const getAutomationCustomObjectCategories = ({ categories }) =>
  categories;
export const getAutomationModelName = ({ automation }) =>
  automation.customObject?.name;

export const getAutomationModelId = ({ automation }) =>
  automation.customObject?.id;

export const getAutomationType = ({ automation }) => automation?.type;

export const getAutomationId = ({ automation }) => automation.id;

export const getDescriptionQues = createSelector(
  ({ conditionDescriptionQue }) => conditionDescriptionQue,
  ({ goalDescriptionQue }) => goalDescriptionQue,
  ({ triggerDescriptionQue }) => triggerDescriptionQue,
  ({ actionDescriptionQue }) => actionDescriptionQue,
  ({ delayDescriptionQue }) => delayDescriptionQue,
  ({ variableDescriptionQue }) => variableDescriptionQue,
  (
    conditionDescriptionQue,
    goalDescriptionQue,
    triggerDescriptionQue,
    actionDescriptionQue,
    delayDescriptionQue,
    variableDescriptionQue
  ) => ({
    conditionDescriptionQue,
    goalDescriptionQue,
    triggerDescriptionQue,
    actionDescriptionQue,
    delayDescriptionQue,
    variableDescriptionQue,
  })
);

export const getDescriptionQueKey = ({ descriptionQueKey }) =>
  descriptionQueKey;

export const getIsForCustomObject = ({ automation }) =>
  !!automation.customObject &&
  automation?.customObject?.name?.toLowerCase() !==
    getObjectModelName('contacts');

export const getEditStep = ({ editStepInfo }) => {
  return editStepInfo && editStepInfo.step;
};
export const getCreateStepInfo = ({ createStepInfo }) => createStepInfo;
export const getInterrupting = ({ interrupting }) => interrupting;
export const getMultiInterrupting = ({ multiInterrupting }) =>
  multiInterrupting;
export const getStepToDelete = ({ stepToDelete }) => stepToDelete;
export const getStepToSkip = ({ skipModal }) => skipModal.step;

export const getToast = ({ toast }) => toast;

export const getGroupedOptionsSelector = (excludedTypes = []) =>
  createSelector(
    (state) => state.steps,
    (state) => state.triggers,
    (stepsCollection, triggers) => {
      return {
        ...Object.values(stepsCollection).reduce((collect, action) => {
          const stepNodeType = getStepConfig(action.type).stepNodeType;
          if (excludedTypes.includes(action.type) || action?.loopDepth) {
            return collect;
          }

          collect[stepNodeType] = [
            ...(collect[stepNodeType] || []),
            { ...action },
          ];

          return collect;
        }, {}),
        [STEP_NODE_TYPES.TRIGGER]: [...triggers],
      };
    }
  );

export const getDeleteModal = ({ deleteModal }) => deleteModal;

export const getDeleteLoopModal = ({ deleteLoopModal }) => deleteLoopModal;

export const getInvalidLoopDropModal = ({ invalidLoopDropModal }) =>
  invalidLoopDropModal;
export const getDeleteBranchModal = ({ deleteBranchModal }) =>
  deleteBranchModal;

export const getStepsInMovingBranch = ({ stepsInMovingBranch }) =>
  stepsInMovingBranch;

export const getStepsAtMaxLoopDepth = ({ stepsAtMaxLoopDepth }) =>
  stepsAtMaxLoopDepth;

export const getStepsOutsideExitLoop = ({ stepsOutsideExitLoop }) =>
  stepsOutsideExitLoop;

export const getPlacingExitLoopSteps = ({ placingExitLoopSteps }) =>
  placingExitLoopSteps;

export const getCopiedStep = ({ copiedStep }) => copiedStep;

export const getVisibleAddWhileMoving = ({ visibleAddWhileMoving }) =>
  visibleAddWhileMoving;

export const getMovingModalState = ({ movingConditionModal }) =>
  movingConditionModal.show;

export const getDropOnLastInBranch = ({ movingConditionModal }) =>
  movingConditionModal.isDropOnLastInBranch;

export const getMovingModalType = ({ movingConditionModal }) =>
  movingConditionModal.type;

export const getMovingModalPayload = ({ movingConditionModal }) =>
  movingConditionModal.payload;

export const getValidationState = ({ validationState }) => validationState;

export const getMergeArgs = createSelector(
  ({ automation }) => automation,
  (automation) => {
    const { customObject } = automation;
    return {
      isForCustomObject:
        isTypeRecordBased(automation) &&
        customObject?.name.toLowerCase() !== getObjectModelName('contacts'),
      customObjectClassName: customObject?.objectClass,
      customObject,
      entityName: customObject?.entityName,
      isGlobalAutomation: isTypeGlobal(automation),
    };
  }
);

export const getErrorCount = ({ errorSteps }) => (errorSteps || []).length > 0;

export const getErrorDebug = ({ errorSteps, deletedSteps }) =>
  `${errorSteps.length} [${(errorSteps || []).join(',')}] [${(
    deletedSteps || []
  ).join(',')}]`;

export const getError = ({ errorSteps }) => errorSteps;

export const getAllowNoChildStep = ({ allowNoChildStep }) => allowNoChildStep;

export const getNewGotoStep = ({ newGotoStep }) => newGotoStep;

export const getCurrenEditingStep = ({ editStepInfo }) => editStepInfo?.step;

export const getNameError = ({ nameError }) => nameError;

/**
 * Compose all mappers and filters
 *
 * @param fields
 * @param categories
 * @returns [... , {...}]
 */
export const getAutomationCategorizedTeamMemberFields = ({
  fields,
  categories,
}) => {
  return compose(
    (categories) => categories.filter(({ options = [] }) => options.length),
    (categories) =>
      categories.map((cat) => {
        return {
          label: cat.name,
          options: cat.fields.map((item) => {
            return {
              label: item.displayName,
              value: item.id,
              item,
            };
          }),
        };
      }),
    (categories) =>
      categories.map((category) => {
        return {
          ...category,
          fields: fields.filter(
            (f) =>
              f.category === category.id &&
              f.fieldType === 'team_selector' &&
              !isOwnerField(f)
          ),
        };
      })
  )(categories);
};

export const getShowBlocker = ({ persisting }) => persisting !== 0;

// this should only be used by flow/index.js
export const getTriggersWithAddMore = createSelector(
  ({ triggers }) => triggers,
  (triggers) => {
    // pass the triggers and add the add_trigger on the end
    return [
      ...triggers,
      {
        detais: {},
        hasError: false,
        id: 'add_trigger',
        prefix: 'trigger',
        stats: { numberComplete: 0 },
        order: triggers.length,
        type: 'add_trigger',
      },
    ];
  }
);

export const getMiniMap = ({ miniMap }) => miniMap;
export const getStepCount = ({ steps, triggers }) =>
  steps.length + triggers.length; // count steps and triggers
export const getHistoryLoaded = ({ historyLoaded }) => historyLoaded;
export const getHistoryFetching = ({ isHistoryFetching }) => isHistoryFetching;
export const getHistoryMeta = ({ historyMeta }) => historyMeta;
export const getHistoryId = ({ historyId }) => historyId;
export const getExecutionMode = ({ executionMode }) => executionMode;
export const getAutomationDebugMode = ({ debugMode }) => debugMode;
export const getCurrentExecution = ({ currentExecution }) => currentExecution;
export const getArchivedContext = createSelector(
  getCurrentExecution,
  (currentExecution) => !!currentExecution?.record?.deleted
);
export const getExecutionRanAsDebug = ({ currentExecution }) =>
  currentExecution?.debug_mode === null ? null : currentExecution?.debug_mode;

export const getWasDebugExecution = ({ currentExecution }) =>
  currentExecution?.debug_mode === 'active';
export const getHistoryErrorDetails = ({ historyErrorDetails }) =>
  historyErrorDetails;

export const getOrderedExecutionHistory = ({ orderedExecutionHistory }) =>
  orderedExecutionHistory;

export const getAutomationIntializationVariables = createSelector(
  getVariables,
  (variables) =>
    variables
      .filter(({ stepType, type }) => isVariableInitialization(stepType, type))
      .map(
        ({
          details: {
            variable: {
              name,
              dataType,
              dataSubtype,
              isArray,
              isArrayItemVariable,
              sourceArrayVariable,
            },
            sources,
          },
        }) => ({
          label: name,
          value: name,
          meta: {
            dataType,
            dataSubtype,
            sourceType: sources?.[0]?.sourceType,
            isArray,
            isArrayItemVariable,
            sourceArrayVariable,
          },
        })
      )
      .sort((a, b) => String(a.label).localeCompare(b.label))
);
export const getAutomationIntializationVariablesWithoutArrayItems =
  createSelector(getAutomationIntializationVariables, (variables) =>
    variables
      .filter(({ meta }) => {
        return !meta.isArrayItemVariable;
      })
      .sort((a, b) => String(a.label).localeCompare(b.label))
  );

const makeVisibleIntializationVariablesSelector = (showArrayItemVariable) =>
  createSelector(getVariables, (variables) =>
    variables.filter(
      ({ stepType, type, details }) =>
        type === 'initialize_variable_add' ||
        (isVariableInitialization(stepType, type) &&
          (showArrayItemVariable || !details?.variable?.isArrayItemVariable))
    )
  );

// One instance per mode, built once: returning a fresh createSelector() per
// call would hand the caller an empty cache every render, so the filtered
// array would never be reference-stable and everything memoized on it (the
// canvas node/edge build) would redo its work each time.
const visibleIntializationVariablesSelectors = {
  true: makeVisibleIntializationVariablesSelector(true),
  false: makeVisibleIntializationVariablesSelector(false),
};

export const getVisibleIntializationVariables = (
  showArrayItemVariable = false
) => visibleIntializationVariablesSelectors[Boolean(showArrayItemVariable)];

const getSupportMatrix = ({ metaData }) => metaData.support_matrix;

export const checkStepVisible = (stepType) => (state) => {
  const supportMatrix = getSupportMatrix(state);
  const automationType = getAutomationType(state);
  return supportMatrix?.steps?.[stepType]?.[automationType] || false;
};

export const getVisibleSteps = createSelector(
  [getSupportMatrix, getAutomationType],
  (supportMatrix, type) => {
    const visible = Object.entries(supportMatrix?.steps || {}).reduce(
      (prev, [key, value]) => {
        if (value[type]) {
          prev.push(key);
        }
        return prev;
      },
      []
    );

    return visible;
  }
);

export const getVisibleTriggers = createSelector(
  [getSupportMatrix, getAutomationType],
  (supportMatrix, type) => {
    const visible = Object.entries(supportMatrix?.triggers || {}).reduce(
      (prev, [key, value]) => {
        if (value[type]) {
          prev.push(key);
        }
        return prev;
      },
      []
    );

    return visible;
  }
);

export const getVisibleGoals = createSelector(
  [getSupportMatrix, getAutomationType],
  (supportMatrix, type) => {
    const visible = Object.entries(supportMatrix?.goals || {}).reduce(
      (prev, [key, value]) => {
        if (value[type]) {
          prev.push(key);
        }
        return prev;
      },
      []
    );

    return visible;
  }
);

export const getCannotUnmergeModal = ({ cannotUnmergeModal }) =>
  cannotUnmergeModal;

export const getConfirmMergeModal = ({ confirmMergeModal }) =>
  confirmMergeModal;

export const getMovingBranchType = ({ movingBranchType }) => movingBranchType;

export const getBranches = ({ branches }) => branches;

export const getStepMovingBlocker = ({ stepMovingBlocker }) =>
  stepMovingBlocker;
export const getFocusStep = ({ focusStep }) => focusStep;
export const getFocusStepToken = ({ focusStepToken }) => focusStepToken;
export const getFocusStepBySearch = ({ focusStepBySearch }) =>
  focusStepBySearch;
export const getHasNoTriggers = ({ triggers }) =>
  !triggers?.length || (triggers.length === 1 && triggers[0].type === 'manual');
export const getAutomationContextModel = ({ model }) => model;
